import React, { memo, useMemo } from 'react';
import { Stage, Layer, Line, Circle, Text } from 'react-konva';
import { CircularPathType, LabelType, pathColor, PathType, PuckType } from '../constants';
import { useGetCanvasSizeByParentContainer } from '../hooks';
import PuckPin from '../puckPin/draw';
import { getActorPoints, getSpinePoints, getSslrPoints, getUniPoints } from './types';
import { useSelectedCrystalId } from 'redux/hooks/synchrotron';
import { getPinState } from '../utils';

interface LineRendererProps {
  pathType: PathType;
  points: number[][] | CircularPathType[];
  color: string;
}

const PathsRenderer = (props: LineRendererProps) => {
  const { pathType, points, color } = props;
  return (
    <Layer id="drawing-canvas-paths-renderer-wrapper">
      {points.map((point, index) =>
        pathType === PathType.STRAIGHT ? (
          <Line
            id={`drawing-canvas-paths-renderer-line-${index}`}
            key={`puck-path-${index}`}
            points={point}
            stroke={color}
          />
        ) : (
          <Circle
            id={`drawing-canvas-paths-renderer-circle-${index}`}
            key={`puck-path-${index}`}
            radius={point.radius}
            x={point.x}
            y={point.y}
            stroke={color}
          />
        ),
      )}
    </Layer>
  );
};

interface LabelsRendererProps {
  lables: LabelType[];
  pinDiameter: number;
}
const LabelsRenderer = (props: LabelsRendererProps) => {
  const { lables, pinDiameter } = props;
  return (
    <Layer id="drawing-canvas-labels-renderer-layer">
      {lables.map((label, index) => (
        <Text
          id={`drawing-canvas-labels-renderer-layer-text-${index}`}
          key={`puck-label-${index}`}
          x={label.x}
          y={label.y}
          width={pinDiameter}
          height={pinDiameter}
          text={label.text}
          fontSize={pinDiameter / 2}
          fontFamily="Calibri"
          align="center"
          verticalAlign="middle"
          fill={'#313131'}
        />
      ))}
    </Layer>
  );
};

interface Props {
  parentRef?: any;
  puckToShow?: PuckType;
  occupiedPins?: { crystalId: number; pinPosition: number; isDirty }[];
  handleOnPinSelect?: (position: number) => void;
  disabled: boolean;
}

const PuckRenderer = (props: Props) => {
  const { parentRef, puckToShow = '', occupiedPins, handleOnPinSelect, disabled } = props;
  const canvasSize = useGetCanvasSizeByParentContainer(parentRef);
  const crystalId = useSelectedCrystalId();

  const pucks = useMemo(
    () => ({
      [PuckType.ACTOR]: getActorPoints(canvasSize),
      [PuckType.SPINE]: getSpinePoints(canvasSize),
      [PuckType.UNI]: getUniPoints(canvasSize),
      [PuckType.SSLR]: getSslrPoints(canvasSize),
      [PuckType.ALS]: getUniPoints(canvasSize),
    }),
    [canvasSize],
  );

  if (!puckToShow || !parentRef) {
    return (
      <div
        id="drawing-canvas-main-container"
        style={{ width: canvasSize.width, height: canvasSize.height }}
        className={`${disabled ? 'bg-[#e0e0e0]' : 'bg-white'}`}
      />
    );
  }

  const puckPoints = pucks[puckToShow];
  return (
    <Stage
      id="drawing-canvas-puck-points-stage"
      width={canvasSize.width}
      height={canvasSize.height}
      className={`${disabled ? 'bg-[#e0e0e0]' : 'bg-white'}`}
    >
      <PathsRenderer
        color={pathColor}
        points={puckPoints.paths}
        pathType={puckPoints.pathType}
      />

      {puckPoints.nodes.map((point, index) => (
        <PuckPin
          key={`puck-pin-${index}`}
          x={point.x}
          y={point.y}
          pinPosition={index + 1}
          pinRadius={puckPoints.pinRadius}
          pinState={getPinState(occupiedPins, index + 1, crystalId)}
          handleOnPinSelect={handleOnPinSelect}
          disabled={disabled}
        />
      ))}

      <LabelsRenderer lables={puckPoints.labels} pinDiameter={puckPoints.pinRadius * 2} />
    </Stage>
  );
};

export default memo(PuckRenderer);
